import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { SiteProgram } from '../../siteDetailTypes'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit'
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'

const Container = styled.div`
  padding: 1.5rem 0;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const Title = styled(Text)`
  font-weight: 600;
`

const AddButton = styled.button`
  background-color: ${palette.white};
  color: ${palette.text.primary};
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${palette.grey.grey100};
  }
`

const ProgramsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ProgramRow = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1fr 1.5fr 1.5fr 1fr;
  align-items: center;
  padding: 1rem 1.25rem;
  background-color: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.5rem;
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`

const ProgramInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const IconWrapper = styled.div<{ $bgColor: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const ProgramDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const ProgramName = styled(Text)`
  font-weight: 600;
  font-size: 0.9375rem;
`

const ProgramBudget = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.8125rem;
`

const EnrolledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 900px) {
    align-items: flex-start;
  }
`

const EnrolledCount = styled(Text)`
  font-weight: 600;
  font-size: 1.25rem;
`

const EnrolledLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
`

const UtilizationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const UtilizationLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
`

const UtilizationBarContainer = styled.div`
  width: 100%;
  height: 0.5rem;
  background-color: ${palette.grey.grey200};
  border-radius: 0.25rem;
  overflow: hidden;
`

const UtilizationBar = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => $percent}%;
  height: 100%;
  background-color: ${palette.primary.pink800};
  border-radius: 0.25rem;
`

const UtilizationValue = styled(Text)`
  font-weight: 600;
  font-size: 0.875rem;
`

const SpendContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 900px) {
    align-items: flex-start;
  }
`

const SpendValue = styled(Text)`
  font-weight: 600;
  font-size: 1.125rem;
`

const SpendLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
`

const TrendText = styled(Text)`
  color: ${palette.success.green600};
  font-size: 0.8125rem;
`

const WaitlistText = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.8125rem;
`

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`

const ManageButton = styled.button`
  background-color: ${palette.white};
  color: ${palette.text.primary};
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${palette.grey.grey100};
  }
`

const getIconComponent = (icon: string) => {
  switch (icon) {
    case 'dollar':
      return <AttachMoneyIcon style={{ fontSize: '1.25rem' }} />
    case 'train':
      return <DirectionsTransitIcon style={{ fontSize: '1.25rem' }} />
    case 'bike':
      return <DirectionsBikeIcon style={{ fontSize: '1.25rem' }} />
    case 'parking':
      return <LocalParkingIcon style={{ fontSize: '1.25rem' }} />
    case 'car':
      return <DirectionsCarIcon style={{ fontSize: '1.25rem' }} />
    default:
      return <AttachMoneyIcon style={{ fontSize: '1.25rem' }} />
  }
}

const getIconBgColor = (icon: string) => {
  switch (icon) {
    case 'dollar':
      return palette.warning.yellow600
    case 'train':
      return palette.success.green600
    case 'bike':
      return palette.accent.teal500
    case 'parking':
      return palette.secondary.purple500
    case 'car':
      return palette.error.red500
    default:
      return palette.grey.grey500
  }
}

const formatCurrency = (cents: number): string => {
  if (!cents) return '$0'
  const dollars = cents / 100
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(0)}k`.replace('$0k', '$0')
  }
  return `$${dollars.toLocaleString()}`
}

const formatSpend = (amount: number): string => {
  if (!amount) return '$0'
  if (amount >= 1000) {
    return `$${amount.toLocaleString()}`
  }
  return `$${amount}`
}

interface ProgramsTabProps {
  programs: SiteProgram[]
}

const ProgramsTab = ({ programs }: ProgramsTabProps) => {
  return (
    <Container>
      <Header>
        <Title variant='body1'>Active Programs</Title>
        <AddButton>+ Add Program</AddButton>
      </Header>

      <ProgramsList>
        {programs.map((program) => (
          <ProgramRow key={program.id}>
            <ProgramInfo>
              <IconWrapper $bgColor={getIconBgColor(program.icon)}>
                {getIconComponent(program.icon)}
              </IconWrapper>
              <ProgramDetails>
                <ProgramName>{program.name}</ProgramName>
                <ProgramBudget>{program.budget}</ProgramBudget>
              </ProgramDetails>
            </ProgramInfo>

            <EnrolledContainer>
              <EnrolledCount>{program.enrolled}</EnrolledCount>
              <EnrolledLabel>of {program.eligible} enrolled</EnrolledLabel>
            </EnrolledContainer>

            <UtilizationContainer>
              <UtilizationLabel>Utilization</UtilizationLabel>
              <UtilizationBarContainer>
                <UtilizationBar $percent={program.utilization} />
              </UtilizationBarContainer>
              <UtilizationValue>{program.utilization}%</UtilizationValue>
            </UtilizationContainer>

            <SpendContainer>
              <SpendValue>{formatSpend(program.monthlySpend)}</SpendValue>
              <SpendLabel>monthly spend</SpendLabel>
              {program.trend.value > 0 && program.trend.label && (
                <TrendText>{program.trend.label}</TrendText>
              )}
              {program.waitlist && (
                <WaitlistText>Waitlist: {program.waitlist}</WaitlistText>
              )}
            </SpendContainer>

            <ActionsContainer>
              <ManageButton>Manage</ManageButton>
            </ActionsContainer>
          </ProgramRow>
        ))}
      </ProgramsList>
    </Container>
  )
}

export default React.memo(ProgramsTab)
